import { SearchJsonType, SearchResult, HttpProxyConfig } from "../types/config";
import { buildWhere } from "./BuildWhere";
import { proxify } from "./proxy";
import { getDomainGuide, applyDomains } from "./domains";

function isCoords(type: SearchJsonType): boolean {
    return (type as any)?.searchType === "coordinates";
}
function hasQueryLayer(type: SearchJsonType): type is SearchJsonType & { queryLayer: string } {
    return typeof (type as any)?.queryLayer === "string" && (type as any).queryLayer.length > 0;
}

function centerOfGeometry(geom: any): { latitude: number; longitude: number } {
    if (geom?.x != null && geom?.y != null) return { longitude: geom.x, latitude: geom.y };
    if (Array.isArray(geom?.points) && geom.points.length) {
        const [x, y] = geom.points[0];
        return { longitude: x, latitude: y };
    }
    const coords: number[][] =
        (geom?.paths?.flat?.(1) as number[][]) ||
        (geom?.rings?.flat?.(1) as number[][]) ||
        [];
    if (coords.length) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const [x, y] of coords) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
        }
        return { longitude: (minX + maxX) / 2, latitude: (minY + maxY) / 2 };
    }
    return { latitude: 0, longitude: 0 };
}

export async function searchParcels(
    type: SearchJsonType,
    values: Record<string, string>,
    proxy?: HttpProxyConfig
): Promise<SearchResult[]> {
    if (isCoords(type)) {
        const latKey = (type as any).latField ?? "latitude";
        const lonKey = (type as any).longField ?? "longitude";
        const lat = parseFloat(values[latKey] ?? "");
        const lon = parseFloat(values[lonKey] ?? "");
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return [];
        return [
            {
                id: "coords-0",
                label: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
                center: { latitude: lat, longitude: lon },
                attrs: { [latKey]: lat, [lonKey]: lon },
            },
        ];
    }

    if (!hasQueryLayer(type)) {
        throw new Error("Unsupported search type: missing queryLayer");
    }

    const where = buildWhere(type.query.where, values);
    const base = type.queryLayer.replace(/\/$/, "");
    const url = proxify(`${base}/query`, proxy);

    const params = new URLSearchParams({
        f: "json",
        where,
        outFields: (type.query.outFields || ["*"]).join(","),
        orderByFields: (type.query.orderByFields || []).join(","),
        returnGeometry: String(!!type.query.returnGeometry),
        outSR: "4326",
    });

    const res = await fetch(`${url}?${params.toString()}`);
    if (!res.ok) throw new Error(`Search failed: ${res.status} ${res.statusText}`);
    const j = await res.json();
    const feats: any[] = j.features || [];

    // decode coded value domains so RES/HS → Residence/House, etc.
    const domainGuide = await getDomainGuide(base);

    return feats.map((feat, i) => {
        const a = { ...(feat.attributes || {}) };
        applyDomains(a, domainGuide);

        const g = feat.geometry || null;
        const center = g ? centerOfGeometry(g) : { latitude: 0, longitude: 0 };

        return {
            id: String(a.OBJECTID ?? i),
            label: [a.Placename, a.FullAddr, a.PPIN, a.PARCEL_ID].filter(Boolean).join(" — ") || `Result ${i + 1}`,
            center,
            raw: feat,
            attrs: a,
        } as SearchResult;
    });
}
